// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Button,
  Tile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Modal,
  TextArea,
  NumberInput,
  Select,
  SelectItem,
  Tag,
  InlineNotification,
  Loading,
  OverflowMenu,
  OverflowMenuItem,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  ProgressIndicator,
  ProgressStep,
  DatePicker,
  DatePickerInput,
  ComboBox,
  Dropdown,
} from '@carbon/react';
import {
  DocumentExport,
  Analytics,
  Edit,
  Email,
  CheckmarkFilled,
  WarningFilled,
  TimeFilled,
  Download,
  Filter,
  Send,
  Calculator,
} from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface GradeEntry {
  grade_id: string;
  student_id: string;
  student_name: string;
  assignment_id: string;
  assignment_title: string;
  subject: string;
  grade_level: string;
  submission_date: string;
  due_date: string;
  score: number | null;
  max_score: number;
  percentage: number | null;
  letter_grade: string | null;
  status: 'pending' | 'graded' | 'returned' | 'missing' | 'late';
  feedback: string;
  graded_by: string;
  graded_at: string | null;
  weight: number;
}

interface Student {
  student_id: string;
  name: string;
  email: string;
  grade_level: string;
  overall_average: number;
  attendance_rate: number;
  assignments_completed: number;
  assignments_total: number;
}

interface ClassStatistics {
  class_average: number;
  highest_score: number;
  lowest_score: number;
  median_score: number;
  submissions_graded: number;
  submissions_pending: number;
  students_count: number;
}

export const TeacherGradeBook: React.FC = () => {
  const { user, token } = useAuthStore();
  const [grades, setGrades] = useState<GradeEntry[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showGradeModal, setShowGradeModal] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState<GradeEntry | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [classStats, setClassStats] = useState<ClassStatistics | null>(null);
  const [filters, setFilters] = useState({
    subject: 'all',
    status: 'all',
    gradeLevel: 'all',
    dateRange: 'week',
  });

  // Grade form state
  const [gradeForm, setGradeForm] = useState({
    score: 0,
    feedback: '',
    sendNotification: false,
  });

  // Load grades on mount
  useEffect(() => {
    loadGrades();
    loadStudents();
    loadClassStatistics();
  }, [token, filters]);

  const loadGrades = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/grades`, {
        headers: { Authorization: `Bearer ${token}` },
        params: filters,
      });

      if (response.data.success) {
        setGrades(response.data.grades || []);
      }
    } catch (err: any) {
      console.error('Failed to load grades:', err);
      // Mock data for demonstration
      setGrades([
        {
          grade_id: '1',
          student_id: 'student1',
          student_name: 'John Doe',
          assignment_id: 'assign1',
          assignment_title: 'Algebra Quiz - Chapter 5',
          subject: 'Math',
          grade_level: '9',
          submission_date: '2025-09-24T14:30:00',
          due_date: '2025-09-25',
          score: 85,
          max_score: 100,
          percentage: 85,
          letter_grade: 'B',
          status: 'graded',
          feedback: 'Good work on the algebraic expressions!',
          graded_by: 'Teacher Name',
          graded_at: '2025-09-25T10:00:00',
          weight: 10,
        },
        {
          grade_id: '2',
          student_id: 'student2',
          student_name: 'Jane Smith',
          assignment_id: 'assign1',
          assignment_title: 'Algebra Quiz - Chapter 5',
          subject: 'Math',
          grade_level: '9',
          submission_date: '2025-09-24T15:45:00',
          due_date: '2025-09-25',
          score: 92,
          max_score: 100,
          percentage: 92,
          letter_grade: 'A',
          status: 'graded',
          feedback: 'Excellent understanding of concepts!',
          graded_by: 'Teacher Name',
          graded_at: '2025-09-25T11:00:00',
          weight: 10,
        },
        {
          grade_id: '3',
          student_id: 'student3',
          student_name: 'Bob Johnson',
          assignment_id: 'assign1',
          assignment_title: 'Algebra Quiz - Chapter 5',
          subject: 'Math',
          grade_level: '9',
          submission_date: '2025-09-25T16:30:00',
          due_date: '2025-09-25',
          score: null,
          max_score: 100,
          percentage: null,
          letter_grade: null,
          status: 'pending',
          feedback: '',
          graded_by: '',
          graded_at: null,
          weight: 10,
        },
        {
          grade_id: '4',
          student_id: 'student4',
          student_name: 'Alice Williams',
          assignment_id: 'assign2',
          assignment_title: 'Geometry Homework - Week 3',
          subject: 'Math',
          grade_level: '10',
          submission_date: '2025-09-23T09:00:00',
          due_date: '2025-09-23',
          score: 78,
          max_score: 50,
          percentage: 78,
          letter_grade: 'C+',
          status: 'returned',
          feedback: 'Review the section on triangle properties.',
          graded_by: 'Teacher Name',
          graded_at: '2025-09-24T14:00:00',
          weight: 5,
        },
        {
          grade_id: '5',
          student_id: 'student5',
          student_name: 'Charlie Davis',
          assignment_id: 'assign2',
          assignment_title: 'Geometry Homework - Week 3',
          subject: 'Math',
          grade_level: '10',
          submission_date: null,
          due_date: '2025-09-23',
          score: null,
          max_score: 50,
          percentage: null,
          letter_grade: null,
          status: 'missing',
          feedback: '',
          graded_by: '',
          graded_at: null,
          weight: 5,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/students`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setStudents(response.data.students || []);
      }
    } catch (err) {
      // Mock data
      setStudents([
        {
          student_id: 'student1',
          name: 'John Doe',
          email: 'john.doe@example.com',
          grade_level: '9',
          overall_average: 85,
          attendance_rate: 95,
          assignments_completed: 18,
          assignments_total: 20,
        },
        {
          student_id: 'student2',
          name: 'Jane Smith',
          email: 'jane.smith@example.com',
          grade_level: '9',
          overall_average: 92,
          attendance_rate: 98,
          assignments_completed: 20,
          assignments_total: 20,
        },
        {
          student_id: 'student3',
          name: 'Bob Johnson',
          email: 'bob.johnson@example.com',
          grade_level: '9',
          overall_average: 78,
          attendance_rate: 90,
          assignments_completed: 16,
          assignments_total: 20,
        },
      ]);
    }
  };

  const loadClassStatistics = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/class/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setClassStats(response.data.statistics);
      }
    } catch (err) {
      // Mock data
      setClassStats({
        class_average: 85.5,
        highest_score: 98,
        lowest_score: 62,
        median_score: 86,
        submissions_graded: 42,
        submissions_pending: 8,
        students_count: 25,
      });
    }
  };

  const submitGrade = async () => {
    if (!token || !selectedGrade) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/v1/teacher/assignments/${selectedGrade.assignment_id}/grade/${selectedGrade.grade_id}`,
        {
          score: gradeForm.score,
          feedback: gradeForm.feedback,
          send_notification: gradeForm.sendNotification,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setSuccess('Grade submitted successfully');
        setShowGradeModal(false);
        loadGrades();
      }
    } catch (err) {
      console.error('Failed to submit grade:', err);
      // For demonstration, update local state
      setGrades(
        grades.map(g => {
          if (g.grade_id === selectedGrade.grade_id) {
            return {
              ...g,
              score: gradeForm.score,
              percentage: (gradeForm.score / g.max_score) * 100,
              letter_grade: calculateLetterGrade((gradeForm.score / g.max_score) * 100),
              status: 'graded',
              feedback: gradeForm.feedback,
              graded_by: user?.email || 'Teacher',
              graded_at: new Date().toISOString(),
            };
          }
          return g;
        })
      );
      setSuccess('Grade submitted successfully (demo mode)');
      setShowGradeModal(false);
    } finally {
      setLoading(false);
    }
  };

  const calculateLetterGrade = (percentage: number): string => {
    if (percentage >= 93) return 'A';
    if (percentage >= 90) return 'A-';
    if (percentage >= 87) return 'B+';
    if (percentage >= 83) return 'B';
    if (percentage >= 80) return 'B-';
    if (percentage >= 77) return 'C+';
    if (percentage >= 73) return 'C';
    if (percentage >= 70) return 'C-';
    if (percentage >= 67) return 'D+';
    if (percentage >= 63) return 'D';
    if (percentage >= 60) return 'D-';
    return 'F';
  };

  const exportGrades = () => {
    // Create CSV content
    const csvContent = [
      ['Student', 'Assignment', 'Subject', 'Score', 'Max Score', 'Percentage', 'Grade', 'Status', 'Feedback'],
      ...grades.map(g => [
        g.student_name,
        g.assignment_title,
        g.subject,
        g.score || 'N/A',
        g.max_score,
        g.percentage ? `${g.percentage}%` : 'N/A',
        g.letter_grade || 'N/A',
        g.status,
        g.feedback,
      ]),
    ]
      .map(row => row.join(','))
      .join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gradebook_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    setSuccess('Grades exported successfully');
  };

  const bulkGrade = () => {
    // Implement bulk grading functionality
    setError('Bulk grading coming soon');
  };

  const sendReminders = () => {
    // Send reminders for missing assignments
    setSuccess('Reminders sent to students with missing assignments');
  };

  const openGradeModal = (grade: GradeEntry) => {
    setSelectedGrade(grade);
    setGradeForm({
      score: grade.score || 0,
      feedback: grade.feedback || '',
      sendNotification: false,
    });
    setShowGradeModal(true);
  };

  const gradeHeaders = [
    { key: 'student', header: 'Student' },
    { key: 'assignment', header: 'Assignment' },
    { key: 'subject', header: 'Subject' },
    { key: 'submitted', header: 'Submitted' },
    { key: 'score', header: 'Score' },
    { key: 'grade', header: 'Grade' },
    { key: 'status', header: 'Status' },
    { key: 'actions', header: 'Actions' },
  ];

  const gradeRows = grades.map(grade => ({
    id: grade.grade_id,
    student: grade.student_name,
    assignment: grade.assignment_title,
    subject: grade.subject,
    submitted: grade.submission_date ? new Date(grade.submission_date).toLocaleDateString() : 'Not submitted',
    score: grade.score !== null ? `${grade.score}/${grade.max_score}` : '-',
    grade: grade.letter_grade || '-',
    status: grade.status,
    actions: grade,
  }));

  const studentHeaders = [
    { key: 'name', header: 'Student Name' },
    { key: 'grade_level', header: 'Grade' },
    { key: 'average', header: 'Average' },
    { key: 'completion', header: 'Completion' },
    { key: 'attendance', header: 'Attendance' },
    { key: 'actions', header: 'Actions' },
  ];

  const studentRows = students.map(student => ({
    id: student.student_id,
    name: student.name,
    grade_level: `Grade ${student.grade_level}`,
    average: `${student.overall_average}%`,
    completion: `${student.assignments_completed}/${student.assignments_total}`,
    attendance: `${student.attendance_rate}%`,
    actions: student,
  }));

  return (
    <Grid fullWidth>
      <Column span={16}>
        <div
          style={{
            padding: '2rem',
            backgroundColor: 'var(--cds-background)',
            minHeight: '100vh',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <div>
              <h1
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--cds-text-primary)',
                }}
              >
                Grade Book
              </h1>
              <p
                style={{
                  fontSize: '1.125rem',
                  color: 'var(--cds-text-secondary)',
                }}
              >
                Track student progress, manage grades, and generate reports.
              </p>
            </div>
            <div>
              <Button
                kind="primary"
                renderIcon={DocumentExport}
                onClick={exportGrades}
                style={{ marginRight: '0.5rem' }}
              >
                Export Grades
              </Button>
              <Button kind="secondary" renderIcon={Analytics} onClick={() => setActiveTab(2)}>
                View Analytics
              </Button>
            </div>
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {success && (
            <InlineNotification
              kind="success"
              title="Success"
              subtitle={success}
              onClose={() => setSuccess(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {loading && <Loading />}

          {/* Filter Controls */}
          <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem' }}>
            <Dropdown
              id="subject-filter"
              titleText="Subject"
              label="All Subjects"
              items={[
                { id: 'all', text: 'All Subjects' },
                { id: 'math', text: 'Mathematics' },
                { id: 'science', text: 'Science' },
                { id: 'english', text: 'English' },
              ]}
              onChange={(e: any) => setFilters({ ...filters, subject: e.selectedItem.id })}
            />
            <Dropdown
              id="status-filter"
              titleText="Status"
              label="All Status"
              items={[
                { id: 'all', text: 'All Status' },
                { id: 'pending', text: 'Pending' },
                { id: 'graded', text: 'Graded' },
                { id: 'returned', text: 'Returned' },
                { id: 'missing', text: 'Missing' },
              ]}
              onChange={(e: any) => setFilters({ ...filters, status: e.selectedItem.id })}
            />
            <Dropdown
              id="grade-filter"
              titleText="Grade Level"
              label="All Grades"
              items={[
                { id: 'all', text: 'All Grades' },
                ...Array.from({ length: 12 }, (_, i) => ({
                  id: String(i + 1),
                  text: `Grade ${i + 1}`,
                })),
              ]}
              onChange={(e: any) => setFilters({ ...filters, gradeLevel: e.selectedItem.id })}
            />
            <Button
              kind="ghost"
              renderIcon={Filter}
              onClick={() => setFilters({ subject: 'all', status: 'all', gradeLevel: 'all', dateRange: 'week' })}
            >
              Clear Filters
            </Button>
          </div>

          <Tabs selectedIndex={activeTab} onChange={({ selectedIndex }) => setActiveTab(selectedIndex!)}>
            <TabList>
              <Tab>Submissions</Tab>
              <Tab>Students</Tab>
              <Tab>Analytics</Tab>
            </TabList>
            <TabPanels>
              {/* Submissions Tab */}
              <TabPanel>
                <div style={{ marginBottom: '1rem' }}>
                  <Button kind="tertiary" size="sm" onClick={bulkGrade} style={{ marginRight: '0.5rem' }}>
                    Bulk Grade
                  </Button>
                  <Button kind="tertiary" size="sm" renderIcon={Email} onClick={sendReminders}>
                    Send Reminders
                  </Button>
                </div>
                <DataTable rows={gradeRows} headers={gradeHeaders}>
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer title="Assignment Submissions">
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => (
                            <TableRow {...getRowProps({ row })} key={row.id}>
                              {row.cells.map(cell => {
                                if (cell.info.header === 'status') {
                                  return (
                                    <TableCell key={cell.id}>
                                      <Tag
                                        type={
                                          cell.value === 'graded'
                                            ? 'green'
                                            : cell.value === 'pending'
                                              ? 'blue'
                                              : cell.value === 'missing'
                                                ? 'red'
                                                : cell.value === 'returned'
                                                  ? 'teal'
                                                  : 'gray'
                                        }
                                      >
                                        {cell.value === 'graded' && <CheckmarkFilled />}
                                        {cell.value === 'pending' && <TimeFilled />}
                                        {cell.value === 'missing' && <WarningFilled />}
                                        {cell.value}
                                      </Tag>
                                    </TableCell>
                                  );
                                }
                                if (cell.info.header === 'grade') {
                                  const percentage = (cell.value as any).percentage;
                                  return (
                                    <TableCell key={cell.id}>
                                      {cell.value !== '-' && (
                                        <Tag
                                          type={
                                            percentage >= 90
                                              ? 'green'
                                              : percentage >= 80
                                                ? 'teal'
                                                : percentage >= 70
                                                  ? 'blue'
                                                  : percentage >= 60
                                                    ? 'purple'
                                                    : 'red'
                                          }
                                        >
                                          {cell.value}
                                        </Tag>
                                      )}
                                      {cell.value === '-' && '-'}
                                    </TableCell>
                                  );
                                }
                                if (cell.info.header === 'actions') {
                                  const grade = cell.value as GradeEntry;
                                  return (
                                    <TableCell key={cell.id}>
                                      <OverflowMenu aria-label="Actions">
                                        <OverflowMenuItem
                                          itemText={grade.status === 'graded' ? 'Edit Grade' : 'Grade'}
                                          onClick={() => openGradeModal(grade)}
                                        />
                                        <OverflowMenuItem
                                          itemText="View Details"
                                          onClick={() => {
                                            // View submission details
                                          }}
                                        />
                                        <OverflowMenuItem
                                          itemText="Send Feedback"
                                          onClick={() => {
                                            // Send feedback to student
                                          }}
                                        />
                                      </OverflowMenu>
                                    </TableCell>
                                  );
                                }
                                return <TableCell key={cell.id}>{cell.value}</TableCell>;
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </TabPanel>

              {/* Students Tab */}
              <TabPanel>
                <DataTable rows={studentRows} headers={studentHeaders}>
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer title="Student Overview">
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => (
                            <TableRow {...getRowProps({ row })} key={row.id}>
                              {row.cells.map(cell => {
                                if (cell.info.header === 'average') {
                                  const avg = parseInt(cell.value);
                                  return (
                                    <TableCell key={cell.id}>
                                      <Tag
                                        type={
                                          avg >= 90
                                            ? 'green'
                                            : avg >= 80
                                              ? 'teal'
                                              : avg >= 70
                                                ? 'blue'
                                                : avg >= 60
                                                  ? 'purple'
                                                  : 'red'
                                        }
                                      >
                                        {cell.value}
                                      </Tag>
                                    </TableCell>
                                  );
                                }
                                if (cell.info.header === 'actions') {
                                  return (
                                    <TableCell key={cell.id}>
                                      <Button kind="ghost" size="sm">
                                        View Progress
                                      </Button>
                                    </TableCell>
                                  );
                                }
                                return <TableCell key={cell.id}>{cell.value}</TableCell>;
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </TabPanel>

              {/* Analytics Tab */}
              <TabPanel>
                <Grid>
                  <Column lg={4}>
                    <Tile>
                      <h3>Class Average</h3>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--cds-text-primary)' }}>
                        {classStats?.class_average.toFixed(1)}%
                      </p>
                      <Tag type="green">↑ 3.2% from last week</Tag>
                    </Tile>
                  </Column>
                  <Column lg={4}>
                    <Tile>
                      <h3>Assignments Graded</h3>
                      <p style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--cds-text-primary)' }}>
                        {classStats?.submissions_graded}/
                        {classStats?.submissions_graded + classStats?.submissions_pending}
                      </p>
                      <ProgressIndicator currentIndex={0} spaceEqually style={{ marginTop: '1rem' }}>
                        <ProgressStep
                          label="Graded"
                          description={`${classStats?.submissions_graded} assignments`}
                          complete
                        />
                        <ProgressStep
                          label="Pending"
                          description={`${classStats?.submissions_pending} assignments`}
                          current
                        />
                      </ProgressIndicator>
                    </Tile>
                  </Column>
                  <Column lg={4}>
                    <Tile>
                      <h3>Score Distribution</h3>
                      <div style={{ marginTop: '1rem' }}>
                        <p>Highest: {classStats?.highest_score}%</p>
                        <p>Median: {classStats?.median_score}%</p>
                        <p>Lowest: {classStats?.lowest_score}%</p>
                      </div>
                    </Tile>
                  </Column>
                  <Column lg={4}>
                    <Tile>
                      <h3>Grade Distribution</h3>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                        <Tag type="green">A: 8 students</Tag>
                        <Tag type="teal">B: 10 students</Tag>
                        <Tag type="blue">C: 5 students</Tag>
                        <Tag type="purple">D: 2 students</Tag>
                        <Tag type="red">F: 0 students</Tag>
                      </div>
                    </Tile>
                  </Column>
                </Grid>
              </TabPanel>
            </TabPanels>
          </Tabs>

          {/* Grade Modal */}
          <Modal
            open={showGradeModal}
            onRequestClose={() => setShowGradeModal(false)}
            onRequestSubmit={submitGrade}
            modalHeading={selectedGrade?.status === 'graded' ? 'Edit Grade' : 'Submit Grade'}
            primaryButtonText="Save Grade"
            secondaryButtonText="Cancel"
          >
            {selectedGrade && (
              <div>
                <div style={{ marginBottom: '1rem' }}>
                  <p>
                    <strong>Student:</strong> {selectedGrade.student_name}
                  </p>
                  <p>
                    <strong>Assignment:</strong> {selectedGrade.assignment_title}
                  </p>
                  <p>
                    <strong>Max Score:</strong> {selectedGrade.max_score} points
                  </p>
                  {selectedGrade.submission_date && (
                    <p>
                      <strong>Submitted:</strong> {new Date(selectedGrade.submission_date).toLocaleString()}
                    </p>
                  )}
                </div>

                <NumberInput
                  id="score"
                  label="Score"
                  value={gradeForm.score}
                  onChange={(e: any, state: any) => setGradeForm({ ...gradeForm, score: state.value })}
                  min={0}
                  max={selectedGrade.max_score}
                  helperText={`Percentage: ${((gradeForm.score / selectedGrade.max_score) * 100).toFixed(1)}% - Grade: ${calculateLetterGrade((gradeForm.score / selectedGrade.max_score) * 100)}`}
                />

                <div style={{ marginTop: '1rem' }}>
                  <TextArea
                    labelText="Feedback"
                    placeholder="Provide feedback for the student"
                    value={gradeForm.feedback}
                    onChange={e => setGradeForm({ ...gradeForm, feedback: e.target.value })}
                    rows={4}
                  />
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={gradeForm.sendNotification}
                      onChange={e => setGradeForm({ ...gradeForm, sendNotification: e.target.checked })}
                    />{' '}
                    Send notification to student
                  </label>
                </div>
              </div>
            )}
          </Modal>
        </div>
      </Column>
    </Grid>
  );
};

export default TeacherGradeBook;
